import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import Draggable from 'react-draggable';
import { PlaceholderPosition, PageBackground, QuoteTemplate } from '@/types/quote';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuoteTemplates } from '@/hooks/useQuoteTemplates';
import { DesignerToolbar } from '@/components/crm/quote/DesignerToolbar';
import { DesignerLeftSidebar } from '@/components/crm/quote/DesignerLeftSidebar';
import { DesignerRightSidebar } from '@/components/crm/quote/DesignerRightSidebar';
import { PageOrderPanel } from '@/components/crm/quote/PageOrderPanel';
import { getAllPlaceholderKeys, getSampleData, createDefaultPlaceholder } from '@/lib/pdfTemplateRenderer';
import { cn } from '@/lib/utils';
import { ArrowLeft, Save, Loader2, FileText, Eye, EyeOff, ImageIcon, Table, Globe, Type } from 'lucide-react';
import { toast } from 'sonner';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const SNAP_THRESHOLD = 8;

export default function CRMQuoteTemplateDesign() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  
  const { templates, loading: templatesLoading, updateTemplate } = useQuoteTemplates();
  const template = templates.find(t => t.id === templateId);
  
  // Core state
  const [pdfPageCount, setPdfPageCount] = useState(0); // Actual number of pages in the PDF background
  const [numPages, setNumPages] = useState(template?.pdf_template_url ? 0 : (template?.page_count || 1));
  const [currentPage, setCurrentPage] = useState(1);
  const [localPageCount, setLocalPageCount] = useState(template?.page_count || 1);
  const [localPageBackgrounds, setLocalPageBackgrounds] = useState<PageBackground[]>(
    template?.page_backgrounds?.length ? template.page_backgrounds : [{ page: 1, type: 'blank', color: '#ffffff' }]
  );
  
  // Determine if we should render a PDF page or a blank page
  const shouldRenderPdfPage = !!(
    template?.pdf_template_url && 
    pdfPageCount > 0 && 
    currentPage <= pdfPageCount
  );
  
  // Undo/Redo for placeholders
  const {
    state: placeholders,
    set: setPlaceholdersWithHistory,
    setWithoutHistory: setPlaceholdersLive,
    commit: commitPlaceholderHistory,
    undo: undoPlaceholders,
    redo: redoPlaceholders,
    reset: resetPlaceholders,
    canUndo,
    canRedo
  } = useUndoRedo<PlaceholderPosition[]>(template?.placeholder_positions || [], { maxHistory: 50 });
  
  const setPlaceholders = setPlaceholdersWithHistory;
  
  // UI state
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [scale, setScale] = useState(1);
  const [saving, setSaving] = useState(false);
  const [clipboard, setClipboard] = useState<PlaceholderPosition[]>([]);
  const [staticTextCounter, setStaticTextCounter] = useState(1);
  const [newStaticText, setNewStaticText] = useState('');
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  
  // Grid settings
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(10);
  const [nudgeStepSize, setNudgeStepSize] = useState(10);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [snapGuides, setSnapGuides] = useState<{ vertical: number[]; horizontal: number[] }>({ vertical: [], horizontal: [] });
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfPageRef = useRef<HTMLDivElement>(null);
  const dragSessionRef = useRef<{
    isDragging: boolean;
    selectedIndices: Set<number>;
    startPositions: Map<number, { x: number; y: number }>;
  } | null>(null);
  const isDraggingPlaceholderRef = useRef(false);
  const rafPendingRef = useRef(false);

  // Update state when template loads
  useEffect(() => {
    if (template) {
      setLocalPageCount(template.page_count || 1);
      setLocalPageBackgrounds(template.page_backgrounds?.length ? template.page_backgrounds : [{ page: 1, type: 'blank', color: '#ffffff' }]);
      if (!template.pdf_template_url) {
        setNumPages(template.page_count || 1);
        setPdfPageCount(0); // Reset PDF page count when no PDF
      } else {
        // Reset pdfPageCount to trigger re-load
        setPdfPageCount(0);
      }
      // Reset placeholders when template loads
      if (template.placeholder_positions && template.placeholder_positions.length > 0) {
        resetPlaceholders(template.placeholder_positions);
      }
    }
  }, [template?.id, resetPlaceholders]); // Use template?.id to avoid re-running on every template update

  // Auto-fit PDF to container
  useEffect(() => {
    const calculateOptimalScale = () => {
      if (!containerRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      const availableWidth = clientWidth - 48;
      const availableHeight = clientHeight - 48;
      const scaleX = availableWidth / PAGE_WIDTH;
      const scaleY = availableHeight / PAGE_HEIGHT;
      const optimalScale = Math.min(scaleX, scaleY, 1.5);
      setScale(Math.max(0.5, optimalScale));
    };

    calculateOptimalScale();
    const resizeObserver = new ResizeObserver(calculateOptimalScale);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Utility functions
  const snapToGridValue = (value: number, gridSize: number): number => Math.round(value / gridSize) * gridSize;
  const snapPosition = (x: number, y: number) => snapToGrid ? { x: snapToGridValue(x, gridSize), y: snapToGridValue(y, gridSize) } : { x, y };
  const snapDimensions = (width: number, height: number) => snapToGrid ? { width: snapToGridValue(width, gridSize), height: snapToGridValue(height, gridSize) } : { width, height };
  
  const clampToPage = (x: number, y: number, width: number, height: number) => ({
    x: Math.max(0, Math.min(x, PAGE_WIDTH - width)),
    y: Math.max(0, Math.min(y, PAGE_HEIGHT - height))
  });

  const commitSnap = (indices: Set<number>) => {
    setPlaceholders(prev => {
      const updated = [...prev];
      indices.forEach(i => {
        const p = updated[i];
        const snapped = snapPosition(p.x, p.y);
        const clamped = clampToPage(snapped.x, snapped.y, p.width, p.height);
        updated[i] = { ...updated[i], x: clamped.x, y: clamped.y };
      });
      return updated;
    });
  };

  // Selection helpers
  const isSelected = (index: number) => selectedIndices.has(index);
  const clearSelection = () => { setSelectedIndices(new Set()); setLastSelectedIndex(null); };
  const selectMultiple = (indices: number[]) => setSelectedIndices(new Set(indices));

  const toggleSelection = (index: number, ctrlKey: boolean, shiftKey: boolean) => {
    if (isDraggingPlaceholderRef.current) return;
    if (shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const newSelection = new Set(selectedIndices);
      for (let i = start; i <= end; i++) if (i < placeholders.length) newSelection.add(i);
      setSelectedIndices(newSelection);
    } else if (ctrlKey) {
      const newSelection = new Set(selectedIndices);
      if (newSelection.has(index)) newSelection.delete(index); else newSelection.add(index);
      setSelectedIndices(newSelection);
      setLastSelectedIndex(index);
    } else {
      setSelectedIndices(new Set([index]));
      setLastSelectedIndex(index);
    }
  };

  const deleteSelected = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => prev.filter((_, i) => !selectedIndices.has(i)));
    clearSelection();
    toast.success(`${selectedIndices.size} veld(en) verwijderd`);
  };

  // Copy/paste
  const copySelected = useCallback(() => {
    if (selectedIndices.size === 0) return;
    const copied = Array.from(selectedIndices).map(i => ({ ...placeholders[i] }));
    setClipboard(copied);
    toast.success(`${copied.length} veld(en) gekopieerd`);
  }, [selectedIndices, placeholders]);

  const pasteClipboard = useCallback(() => {
    if (clipboard.length === 0) {
      toast.info('Niets om te plakken');
      return;
    }
    const newPlaceholders = clipboard.map(p => ({
      ...p,
      page: currentPage,
      x: p.page === currentPage ? Math.min(PAGE_WIDTH - p.width, p.x + 15) : p.x,
      y: p.page === currentPage ? Math.min(PAGE_HEIGHT - p.height, p.y + 15) : p.y,
      isGlobal: false,
    }));
    const startIndex = placeholders.length;
    setPlaceholders(prev => [...prev, ...newPlaceholders]);
    setSelectedIndices(new Set(newPlaceholders.map((_, i) => startIndex + i)));
    toast.success(`${newPlaceholders.length} veld(en) geplakt`);
  }, [clipboard, currentPage, placeholders.length, setPlaceholders]);

  // Static text
  const addStaticText = useCallback((text: string) => {
    const key = `{{static_text_${staticTextCounter}}}`;
    setStaticTextCounter(prev => prev + 1);
    const newPlaceholder: PlaceholderPosition = {
      key,
      page: currentPage,
      x: 50, y: 50, width: 100, height: 25,
      fontSize: 12, fontWeight: 'normal', color: '#000000', align: 'left',
      staticValue: text
    };
    const startIndex = placeholders.length;
    setPlaceholders(prev => [...prev, newPlaceholder]);
    setSelectedIndices(new Set([startIndex]));
    setLastSelectedIndex(startIndex);
    toast.success(`Vaste tekst "${text}" toegevoegd`);
  }, [staticTextCounter, currentPage, placeholders.length, setPlaceholders]);

  // Nudge
  const handleNudge = (direction: 'up' | 'down' | 'left' | 'right', amount: number) => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      selectedIndices.forEach(i => {
        const p = updated[i];
        let newX = p.x, newY = p.y;
        switch (direction) {
          case 'up': newY = Math.max(0, p.y - amount); break;
          case 'down': newY = Math.min(PAGE_HEIGHT - p.height, p.y + amount); break;
          case 'left': newX = Math.max(0, p.x - amount); break;
          case 'right': newX = Math.min(PAGE_WIDTH - p.width, p.x + amount); break;
        }
        const snapped = snapPosition(newX, newY);
        updated[i] = { ...updated[i], x: snapped.x, y: snapped.y };
      });
      return updated;
    });
  };

  const handleCenter = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      selectedIndices.forEach(i => {
        const p = updated[i];
        const centerX = (PAGE_WIDTH - p.width) / 2;
        const centerY = (PAGE_HEIGHT - p.height) / 2;
        const snapped = snapPosition(centerX, centerY);
        updated[i] = { ...updated[i], x: snapped.x, y: snapped.y };
      });
      return updated;
    });
    toast.success('Gecentreerd');
  };

  // Alignment functions
  const alignLeft = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], x: snapToGridValue(0, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minX = Math.min(...selected.map(p => p.x));
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], x: snapToGridValue(minX, gridSize) }; });
      }
      return updated;
    });
    toast.success('Links uitgelijnd');
  };

  const alignRight = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], x: snapToGridValue(PAGE_WIDTH - updated[idx].width, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const maxX = Math.max(...selected.map(p => p.x + p.width));
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], x: snapToGridValue(maxX - updated[i].width, gridSize) }; });
      }
      return updated;
    });
    toast.success('Rechts uitgelijnd');
  };

  const alignTop = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], y: snapToGridValue(0, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minY = Math.min(...selected.map(p => p.y));
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], y: snapToGridValue(minY, gridSize) }; });
      }
      return updated;
    });
    toast.success('Boven uitgelijnd');
  };

  const alignBottom = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], y: snapToGridValue(PAGE_HEIGHT - updated[idx].height, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const maxY = Math.max(...selected.map(p => p.y + p.height));
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], y: snapToGridValue(maxY - updated[i].height, gridSize) }; });
      }
      return updated;
    });
    toast.success('Onder uitgelijnd');
  };

  const alignCenterH = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], x: snapToGridValue((PAGE_WIDTH - updated[idx].width) / 2, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minX = Math.min(...selected.map(p => p.x));
        const maxX = Math.max(...selected.map(p => p.x + p.width));
        const centerX = (minX + maxX) / 2;
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], x: snapToGridValue(centerX - updated[i].width / 2, gridSize) }; });
      }
      return updated;
    });
    toast.success('Horizontaal gecentreerd');
  };

  const alignCenterV = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      if (selectedIndices.size === 1) {
        const idx = Array.from(selectedIndices)[0];
        updated[idx] = { ...updated[idx], y: snapToGridValue((PAGE_HEIGHT - updated[idx].height) / 2, gridSize) };
      } else {
        const selected = Array.from(selectedIndices).map(i => placeholders[i]);
        const minY = Math.min(...selected.map(p => p.y));
        const maxY = Math.max(...selected.map(p => p.y + p.height));
        const centerY = (minY + maxY) / 2;
        selectedIndices.forEach(i => { updated[i] = { ...updated[i], y: snapToGridValue(centerY - updated[i].height / 2, gridSize) }; });
      }
      return updated;
    });
    toast.success('Verticaal gecentreerd');
  };

  const distributeHorizontally = () => {
    if (selectedIndices.size < 3) { toast.error('Minimaal 3 velden'); return; }
    const selected = Array.from(selectedIndices).map(i => ({ index: i, placeholder: placeholders[i] })).sort((a, b) => a.placeholder.x - b.placeholder.x);
    const first = selected[0].placeholder;
    const last = selected[selected.length - 1].placeholder;
    const totalSpace = (last.x + last.width) - first.x;
    const totalWidth = selected.reduce((sum, s) => sum + s.placeholder.width, 0);
    const gap = (totalSpace - totalWidth) / (selected.length - 1);
    setPlaceholders(prev => {
      const updated = [...prev];
      let currentX = first.x;
      selected.forEach(({ index, placeholder }) => {
        updated[index] = { ...updated[index], x: snapToGridValue(currentX, gridSize) };
        currentX += placeholder.width + gap;
      });
      return updated;
    });
    toast.success('Horizontaal verdeeld');
  };

  const distributeVertically = () => {
    if (selectedIndices.size < 3) { toast.error('Minimaal 3 velden'); return; }
    const selected = Array.from(selectedIndices).map(i => ({ index: i, placeholder: placeholders[i] })).sort((a, b) => a.placeholder.y - b.placeholder.y);
    const first = selected[0].placeholder;
    const last = selected[selected.length - 1].placeholder;
    const totalSpace = (last.y + last.height) - first.y;
    const totalHeight = selected.reduce((sum, s) => sum + s.placeholder.height, 0);
    const gap = (totalSpace - totalHeight) / (selected.length - 1);
    setPlaceholders(prev => {
      const updated = [...prev];
      let currentY = first.y;
      selected.forEach(({ index, placeholder }) => {
        updated[index] = { ...updated[index], y: snapToGridValue(currentY, gridSize) };
        currentY += placeholder.height + gap;
      });
      return updated;
    });
    toast.success('Verticaal verdeeld');
  };

  // Toggle global for selected
  const toggleGlobal = () => {
    if (selectedIndices.size === 0) return;
    setPlaceholders(prev => {
      const updated = [...prev];
      const allGlobal = Array.from(selectedIndices).every(i => updated[i].isGlobal);
      selectedIndices.forEach(i => { updated[i] = { ...updated[i], isGlobal: !allGlobal }; });
      return updated;
    });
    toast.success('Globaal status gewijzigd');
  };

  // Magnetic snap calculation
  const calculateSnapGuides = useCallback((draggedIndex: number, newX: number, newY: number, width: number, height: number) => {
    const otherPlaceholders = placeholders.filter((p, i) => i !== draggedIndex && p.page === currentPage);
    let snappedX = newX, snappedY = newY;
    const activeGuides = { vertical: [] as number[], horizontal: [] as number[] };
    
    const dragEdges = { left: newX, right: newX + width, centerX: newX + width / 2, top: newY, bottom: newY + height, centerY: newY + height / 2 };
    
    for (const other of otherPlaceholders) {
      const otherEdges = { left: other.x, right: other.x + other.width, centerX: other.x + other.width / 2, top: other.y, bottom: other.y + other.height, centerY: other.y + other.height / 2 };
      
      if (Math.abs(dragEdges.left - otherEdges.left) < SNAP_THRESHOLD) { snappedX = otherEdges.left; activeGuides.vertical.push(otherEdges.left); }
      if (Math.abs(dragEdges.right - otherEdges.right) < SNAP_THRESHOLD) { snappedX = otherEdges.right - width; activeGuides.vertical.push(otherEdges.right); }
      if (Math.abs(dragEdges.centerX - otherEdges.centerX) < SNAP_THRESHOLD) { snappedX = otherEdges.centerX - width / 2; activeGuides.vertical.push(otherEdges.centerX); }
      if (Math.abs(dragEdges.top - otherEdges.top) < SNAP_THRESHOLD) { snappedY = otherEdges.top; activeGuides.horizontal.push(otherEdges.top); }
      if (Math.abs(dragEdges.bottom - otherEdges.bottom) < SNAP_THRESHOLD) { snappedY = otherEdges.bottom - height; activeGuides.horizontal.push(otherEdges.bottom); }
      if (Math.abs(dragEdges.centerY - otherEdges.centerY) < SNAP_THRESHOLD) { snappedY = otherEdges.centerY - height / 2; activeGuides.horizontal.push(otherEdges.centerY); }
    }
    
    // Page edges and center
    if (Math.abs(dragEdges.left) < SNAP_THRESHOLD) { snappedX = 0; activeGuides.vertical.push(0); }
    if (Math.abs(dragEdges.right - PAGE_WIDTH) < SNAP_THRESHOLD) { snappedX = PAGE_WIDTH - width; activeGuides.vertical.push(PAGE_WIDTH); }
    if (Math.abs(dragEdges.centerX - PAGE_WIDTH / 2) < SNAP_THRESHOLD) { snappedX = PAGE_WIDTH / 2 - width / 2; activeGuides.vertical.push(PAGE_WIDTH / 2); }
    if (Math.abs(dragEdges.top) < SNAP_THRESHOLD) { snappedY = 0; activeGuides.horizontal.push(0); }
    if (Math.abs(dragEdges.bottom - PAGE_HEIGHT) < SNAP_THRESHOLD) { snappedY = PAGE_HEIGHT - height; activeGuides.horizontal.push(PAGE_HEIGHT); }
    if (Math.abs(dragEdges.centerY - PAGE_HEIGHT / 2) < SNAP_THRESHOLD) { snappedY = PAGE_HEIGHT / 2 - height / 2; activeGuides.horizontal.push(PAGE_HEIGHT / 2); }
    
    return { x: snappedX, y: snappedY, guides: activeGuides };
  }, [placeholders, currentPage]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const isTyping = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';
      if (isTyping) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) { if (canRedo) { redoPlaceholders(); toast.info('Opnieuw'); } }
        else { if (canUndo) { undoPlaceholders(); toast.info('Ongedaan gemaakt'); } }
        return;
      }
      if (e.altKey && selectedIndices.size > 0) {
        switch (e.key.toLowerCase()) {
          case 'l': e.preventDefault(); alignLeft(); return;
          case 'r': e.preventDefault(); alignRight(); return;
          case 't': e.preventDefault(); alignTop(); return;
          case 'b': e.preventDefault(); alignBottom(); return;
          case 'c': e.preventDefault(); alignCenterH(); alignCenterV(); return;
        }
      }
      if (e.key === 'c' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); copySelected(); return; }
      if (e.key === 'v' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); pasteClipboard(); return; }
      if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (selectedIndices.size > 0) {
          const duplicates = Array.from(selectedIndices).map(i => ({
            ...placeholders[i],
            x: Math.min(PAGE_WIDTH - placeholders[i].width, placeholders[i].x + 15),
            y: Math.min(PAGE_HEIGHT - placeholders[i].height, placeholders[i].y + 15),
            isGlobal: false,
          }));
          const startIndex = placeholders.length;
          setPlaceholders(prev => [...prev, ...duplicates]);
          setSelectedIndices(new Set(duplicates.map((_, i) => startIndex + i)));
          toast.success(`${duplicates.length} veld(en) gedupliceerd`);
        }
        return;
      }
      if (e.key === 'g' && !e.ctrlKey && !e.metaKey) {
        setSnapToGrid(prev => { toast.info(`Grid ${!prev ? 'aan' : 'uit'}`); return !prev; });
      }
      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const pageIndices = placeholders.map((p, i) => (p.page === currentPage || p.isGlobal ? i : -1)).filter(i => i !== -1);
        selectMultiple(pageIndices);
        toast.info(`${pageIndices.length} veld(en) geselecteerd`);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIndices.size > 0) { e.preventDefault(); deleteSelected(); }
      if (e.key === 'Escape') clearSelection();
      if (selectedIndices.size > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const amount = e.shiftKey ? nudgeStepSize * 2 : (snapToGrid ? gridSize : 1);
        switch (e.key) {
          case 'ArrowUp': handleNudge('up', amount); break;
          case 'ArrowDown': handleNudge('down', amount); break;
          case 'ArrowLeft': handleNudge('left', amount); break;
          case 'ArrowRight': handleNudge('right', amount); break;
        }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [snapToGrid, selectedIndices, placeholders, currentPage, nudgeStepSize, gridSize, canUndo, canRedo, copySelected, pasteClipboard, setPlaceholders]);

  // Drop handler
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!pdfPageRef.current) return;
    const key = e.dataTransfer.getData('text/plain');
    if (!key) return;
    const tempPlaceholder = createDefaultPlaceholder(key, 0, 0);
    const centerX = (PAGE_WIDTH - tempPlaceholder.width) / 2;
    const centerY = (PAGE_HEIGHT - tempPlaceholder.height) / 2;
    const snapped = snapPosition(centerX, centerY);
    const newPlaceholder = createDefaultPlaceholder(key, snapped.x, snapped.y);
    newPlaceholder.page = currentPage;
    setPlaceholders(prev => [...prev, newPlaceholder]);
    const newIndex = placeholders.length;
    setSelectedIndices(new Set([newIndex]));
    setLastSelectedIndex(newIndex);
    toast.success(`Veld "${key}" toegevoegd`);
  };

  // Placeholder update handlers
  const handlePlaceholderUpdate = (index: number, updates: Partial<PlaceholderPosition>) => {
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  };

  const handlePlaceholderDelete = (index: number) => {
    setPlaceholders(prev => prev.filter((_, i) => i !== index));
    clearSelection();
    toast.success('Veld verwijderd');
  };

  const handleResize = (index: number, width: number, height: number) => {
    const snapped = snapDimensions(width, height);
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], width: snapped.width, height: snapped.height };
      return updated;
    });
  };

  // Page management
  const handleAddPage = () => {
    const newPage = localPageCount + 1;
    setLocalPageCount(newPage);
    setLocalPageBackgrounds(prev => [...prev, { page: newPage, type: 'blank', color: '#ffffff' }]);
    if (!template?.pdf_template_url) setNumPages(newPage);
    toast.success(`Pagina ${newPage} toegevoegd`);
  };

  const handleDeletePage = (pageNumber: number) => {
    if (localPageCount <= 1) { toast.error('Minimaal 1 pagina'); return; }
    setLocalPageCount(prev => prev - 1);
    setLocalPageBackgrounds(prev => prev.filter(bg => bg.page !== pageNumber).map(bg => bg.page > pageNumber ? { ...bg, page: bg.page - 1 } : bg));
    setPlaceholders(prev => prev.filter(p => p.page !== pageNumber).map(p => p.page > pageNumber ? { ...p, page: p.page - 1 } : p));
    if (currentPage >= pageNumber) setCurrentPage(p => Math.max(1, p - 1));
    if (!template?.pdf_template_url) setNumPages(prev => prev - 1);
    toast.success(`Pagina ${pageNumber} verwijderd`);
  };

  const handlePageReorder = (oldIndex: number, newIndex: number) => {
    if (oldIndex === newIndex) return;
    const fromPage = oldIndex + 1;
    const toPage = newIndex + 1;
    setLocalPageBackgrounds(prev => {
      const items = [...prev];
      const [moved] = items.splice(oldIndex, 1);
      items.splice(newIndex, 0, moved);
      return items.map((bg, i) => ({ ...bg, page: i + 1 }));
    });
    setPlaceholders(prev => prev.map(p => {
      if (p.page === fromPage) return { ...p, page: toPage };
      if (fromPage < toPage && p.page > fromPage && p.page <= toPage) return { ...p, page: p.page - 1 };
      if (fromPage > toPage && p.page >= toPage && p.page < fromPage) return { ...p, page: p.page + 1 };
      return p;
    }));
    toast.success('Pagina volgorde bijgewerkt');
  };

  // Canvas mouse handlers for rectangle selection
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (showPreview || !pdfPageRef.current) return;
    if ((e.target as HTMLElement).closest('.placeholder-item')) return;
    const pdfRect = pdfPageRef.current.getBoundingClientRect();
    const startX = e.clientX - pdfRect.left;
    const startY = e.clientY - pdfRect.top;
    setIsSelecting(true);
    setSelectionRect({ startX, startY, endX: startX, endY: startY });
    if (!e.ctrlKey && !e.metaKey) clearSelection();
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !pdfPageRef.current || !selectionRect) return;
    const pdfRect = pdfPageRef.current.getBoundingClientRect();
    const endX = e.clientX - pdfRect.left;
    const endY = e.clientY - pdfRect.top;
    setSelectionRect({ ...selectionRect, endX, endY });
  };

  const handleCanvasMouseUp = () => {
    if (!isSelecting || !selectionRect) { setIsSelecting(false); setSelectionRect(null); return; }
    const selectionWidth = Math.abs(selectionRect.endX - selectionRect.startX);
    const selectionHeight = Math.abs(selectionRect.endY - selectionRect.startY);
    if (selectionWidth > 5 && selectionHeight > 5) {
      const minX = Math.min(selectionRect.startX, selectionRect.endX) / scale;
      const maxX = Math.max(selectionRect.startX, selectionRect.endX) / scale;
      const minY = Math.min(selectionRect.startY, selectionRect.endY) / scale;
      const maxY = Math.max(selectionRect.startY, selectionRect.endY) / scale;
      const selectedInRect = placeholders.map((p, i) => {
        if (p.page !== currentPage && !p.isGlobal) return -1;
        const pCenterX = p.x + p.width / 2;
        const pCenterY = p.y + p.height / 2;
        if (pCenterX >= minX && pCenterX <= maxX && pCenterY >= minY && pCenterY <= maxY) return i;
        return -1;
      }).filter(i => i !== -1);
      if (selectedInRect.length > 0) {
        const newSelection = new Set(selectedIndices);
        selectedInRect.forEach(i => newSelection.add(i));
        setSelectedIndices(newSelection);
        toast.info(`${selectedInRect.length} veld(en) geselecteerd`);
      }
    }
    setIsSelecting(false);
    setSelectionRect(null);
  };

  // Save handler
  const handleSave = async () => {
    if (!templateId) return;
    try {
      setSaving(true);
      await updateTemplate(templateId, {
        placeholder_positions: placeholders,
        page_count: localPageCount,
        page_backgrounds: localPageBackgrounds,
      });
      toast.success('Template opgeslagen');
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(`/crm/quote-templates/${templateId}`);

  // Computed values
  const effectivePageCount = Math.max(numPages, localPageCount);
  const currentPagePlaceholders = placeholders.filter(p => p.page === currentPage || p.isGlobal === true);
  const currentPageBackground = localPageBackgrounds.find(bg => bg.page === currentPage) || { page: currentPage, type: 'blank' as const, color: '#ffffff' };
  const availablePlaceholders = getAllPlaceholderKeys();

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="h-10 border-b flex items-center justify-between px-3 bg-card flex-shrink-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleBack}>
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">Designer</span>
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">• {template?.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left Sidebar */}
        <div className="flex flex-shrink-0 h-full overflow-hidden">
          <PageOrderPanel
            pageCount={effectivePageCount}
            pageBackgrounds={localPageBackgrounds}
            currentPage={currentPage}
            pdfPageCount={pdfPageCount}
            onPageSelect={setCurrentPage}
            onReorder={handlePageReorder}
            onAddPage={handleAddPage}
            onDeletePage={handleDeletePage}
          />
          <DesignerLeftSidebar
            availablePlaceholders={availablePlaceholders}
            newStaticText={newStaticText}
            setNewStaticText={setNewStaticText}
            addStaticText={addStaticText}
            onDragStart={() => setIsDragging(true)}
          />
        </div>

        {/* PDF Canvas */}
        <div ref={containerRef} className="flex-1 flex items-center justify-center bg-muted/30 p-4 overflow-hidden">
          <div
            ref={pdfPageRef}
            className="relative bg-white shadow-2xl select-none"
            style={{ transformOrigin: 'center center' }}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
          >
            {/* Hidden Document loader to get pdfPageCount - only rendered when we have a PDF URL but haven't loaded page count yet */}
            {template?.pdf_template_url && pdfPageCount === 0 && (
              <Document
                file={template.pdf_template_url}
                onLoadSuccess={({ numPages: pdfPages }) => {
                  console.log(`[Designer] PDF loaded with ${pdfPages} pages`);
                  setPdfPageCount(pdfPages);
                  const desiredPageCount = Math.max(pdfPages, template.page_count || 1);
                  setNumPages(desiredPageCount);
                  setLocalPageCount(desiredPageCount);
                }}
                onLoadError={() => toast.error('PDF laden mislukt')}
                className="absolute opacity-0 pointer-events-none"
              >
                <Page pageNumber={1} width={1} />
              </Document>
            )}

            {/* Actual PDF page - shown only when currentPage is within PDF range */}
            {shouldRenderPdfPage ? (
              <Document
                file={template.pdf_template_url}
                onLoadSuccess={({ numPages: pdfPages }) => {
                  if (pdfPageCount === 0) {
                    console.log(`[Designer] PDF loaded with ${pdfPages} pages`);
                    setPdfPageCount(pdfPages);
                    const desiredPageCount = Math.max(pdfPages, template.page_count || 1);
                    setNumPages(desiredPageCount);
                    setLocalPageCount(desiredPageCount);
                  }
                }}
                onLoadError={() => toast.error('PDF laden mislukt')}
              >
                <Page
                  pageNumber={currentPage}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  width={PAGE_WIDTH * scale}
                />
              </Document>
            ) : (
              <div
                style={{
                  width: PAGE_WIDTH * scale,
                  height: PAGE_HEIGHT * scale,
                  backgroundColor: currentPageBackground.color || '#ffffff',
                  border: '1px solid hsl(var(--border))'
                }}
                className="flex items-center justify-center"
              >
                <div className="text-center text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs opacity-50">
                    {template?.pdf_template_url 
                      ? `Extra pagina ${currentPage}` 
                      : `Blanco pagina ${currentPage}`}
                  </p>
                </div>
              </div>
            )}

            {/* Grid Overlay */}
            {snapToGrid && !showPreview && (
              <svg className="absolute inset-0 pointer-events-none opacity-20" style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}>
                <defs>
                  <pattern id="grid-pattern" x="0" y="0" width={gridSize * scale} height={gridSize * scale} patternUnits="userSpaceOnUse">
                    <circle cx={gridSize * scale / 2} cy={gridSize * scale / 2} r="1" fill="currentColor" className="text-primary" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid-pattern)" />
              </svg>
            )}

            {/* Snap guides */}
            {snapGuides.vertical.map((x, i) => (
              <div key={`v-${i}`} className="absolute top-0 bottom-0 w-px bg-primary/50" style={{ left: x * scale }} />
            ))}
            {snapGuides.horizontal.map((y, i) => (
              <div key={`h-${i}`} className="absolute left-0 right-0 h-px bg-primary/50" style={{ top: y * scale }} />
            ))}

            {/* Placeholders */}
            {!showPreview && currentPagePlaceholders.map((placeholder) => {
              const globalIndex = placeholders.indexOf(placeholder);
              const selected = isSelected(globalIndex);
              return (
                <Draggable
                  key={`${placeholder.key}-${globalIndex}`}
                  position={{ x: placeholder.x * scale, y: placeholder.y * scale }}
                  onStart={() => {
                    commitPlaceholderHistory();
                    const draggingIndices = selected && selectedIndices.size > 1 ? selectedIndices : new Set([globalIndex]);
                    const startPositions = new Map<number, { x: number; y: number }>();
                    draggingIndices.forEach(i => startPositions.set(i, { x: placeholders[i].x, y: placeholders[i].y }));
                    dragSessionRef.current = { isDragging: true, selectedIndices: draggingIndices, startPositions };
                    isDraggingPlaceholderRef.current = true;
                  }}
                  onDrag={(e, data) => {
                    if (!dragSessionRef.current || rafPendingRef.current) return;
                    rafPendingRef.current = true;
                    requestAnimationFrame(() => {
                      rafPendingRef.current = false;
                      if (!dragSessionRef.current) return;
                      const { selectedIndices: draggingIndices, startPositions } = dragSessionRef.current;
                      const anchorStart = startPositions.get(globalIndex);
                      if (!anchorStart) return;
                      const deltaX = (data.x / scale) - anchorStart.x;
                      const deltaY = (data.y / scale) - anchorStart.y;
                      setPlaceholdersLive(prev => {
                        const updated = [...prev];
                        let activeSnapGuides = { vertical: [] as number[], horizontal: [] as number[] };
                        draggingIndices.forEach(i => {
                          const start = startPositions.get(i);
                          if (!start) return;
                          let newX = start.x + deltaX;
                          let newY = start.y + deltaY;
                          const snapResult = calculateSnapGuides(i, newX, newY, updated[i].width, updated[i].height);
                          newX = snapResult.x; newY = snapResult.y;
                          activeSnapGuides.vertical.push(...snapResult.guides.vertical);
                          activeSnapGuides.horizontal.push(...snapResult.guides.horizontal);
                          const clamped = clampToPage(newX, newY, updated[i].width, updated[i].height);
                          updated[i] = { ...updated[i], x: clamped.x, y: clamped.y };
                        });
                        setSnapGuides({ vertical: [...new Set(activeSnapGuides.vertical)], horizontal: [...new Set(activeSnapGuides.horizontal)] });
                        return updated;
                      });
                    });
                  }}
                  onStop={() => {
                    if (!dragSessionRef.current) return;
                    commitSnap(dragSessionRef.current.selectedIndices);
                    setSnapGuides({ vertical: [], horizontal: [] });
                    dragSessionRef.current = null;
                    setTimeout(() => { isDraggingPlaceholderRef.current = false; }, 50);
                  }}
                  bounds="parent"
                >
                  <div
                    className={cn(
                      "border-2 cursor-move transition-all placeholder-item select-none relative",
                      placeholder.key.startsWith('{{static_text_') ? "border-orange-500 bg-orange-500/10" : placeholder.isGlobal ? "border-amber-500 bg-amber-500/10" : "border-primary bg-primary/10",
                      selected && "ring-2 shadow-lg",
                      selected && placeholder.key.startsWith('{{static_text_') && "ring-orange-500 bg-orange-500/20",
                      selected && !placeholder.key.startsWith('{{static_text_') && placeholder.isGlobal && "ring-amber-500 bg-amber-500/20",
                      selected && !placeholder.key.startsWith('{{static_text_') && !placeholder.isGlobal && "ring-primary bg-primary/20"
                    )}
                    style={{ width: placeholder.width * scale, height: placeholder.height * scale, position: 'absolute', left: 0, top: 0 }}
                    onClick={(e) => { e.stopPropagation(); toggleSelection(globalIndex, e.ctrlKey || e.metaKey, e.shiftKey); }}
                  >
                    {placeholder.key.startsWith('{{static_text_') && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 z-10">
                        <Type className="h-2.5 w-2.5" />Tekst
                      </div>
                    )}
                    {placeholder.isGlobal && !placeholder.key.startsWith('{{static_text_') && (
                      <div className="absolute -top-2 -left-2 bg-amber-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 z-10">
                        <Globe className="h-2.5 w-2.5" />Globaal
                      </div>
                    )}
                    {placeholder.key.startsWith('{{static_text_') ? (
                      <div className="flex items-center h-full px-1 overflow-hidden">
                        <span className="text-[10px] font-medium truncate" style={{ color: placeholder.color }}>{placeholder.staticValue || 'Tekst...'}</span>
                      </div>
                    ) : placeholder.key === '{{company_logo}}' ? (
                      <div className="flex items-center justify-center h-full bg-muted/50 gap-1"><ImageIcon className="h-4 w-4 text-muted-foreground" /><span className="text-[10px] font-medium text-muted-foreground">Logo</span></div>
                    ) : placeholder.key === '{{line_items_table}}' ? (
                      <div className="flex flex-col items-center justify-center h-full bg-muted/50 text-center p-1"><Table className="h-5 w-5 text-muted-foreground mb-1" /><span className="text-[10px] font-medium text-muted-foreground">Regelitems Tabel</span></div>
                    ) : (
                      <span className="text-[10px] font-mono px-1 truncate block">{placeholder.key}</span>
                    )}
                    {/* Resize Handle */}
                    <div
                      className="absolute bottom-0 right-0 w-3 h-3 bg-primary cursor-se-resize"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startWidth = placeholder.width;
                        const startHeight = placeholder.height;
                        const handleMouseMove = (moveEvent: MouseEvent) => {
                          const deltaX = (moveEvent.clientX - startX) / scale;
                          const deltaY = (moveEvent.clientY - startY) / scale;
                          handleResize(globalIndex, Math.max(30, startWidth + deltaX), Math.max(15, startHeight + deltaY));
                        };
                        const handleMouseUp = () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                    />
                  </div>
                </Draggable>
              );
            })}

            {/* Preview mode */}
            {showPreview && currentPagePlaceholders.map((placeholder) => {
              const globalIndex = placeholders.indexOf(placeholder);
              return (
                <div
                  key={`preview-${placeholder.key}-${globalIndex}`}
                  className="absolute overflow-hidden"
                  style={{
                    left: placeholder.x * scale,
                    top: placeholder.y * scale,
                    width: placeholder.width * scale,
                    height: placeholder.height * scale,
                    fontSize: placeholder.fontSize * scale,
                    fontWeight: placeholder.fontWeight,
                    color: placeholder.color,
                    textAlign: placeholder.align,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: placeholder.align === 'center' ? 'center' : placeholder.align === 'right' ? 'flex-end' : 'flex-start',
                  }}
                >
                  {placeholder.key.startsWith('{{static_text_') ? placeholder.staticValue : getSampleData(placeholder.key)}
                </div>
              );
            })}

            {/* Selection rectangle */}
            {isSelecting && selectionRect && (
              <div
                className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                style={{
                  left: Math.min(selectionRect.startX, selectionRect.endX),
                  top: Math.min(selectionRect.startY, selectionRect.endY),
                  width: Math.abs(selectionRect.endX - selectionRect.startX),
                  height: Math.abs(selectionRect.endY - selectionRect.startY),
                }}
              />
            )}

            {/* Drop zone hint */}
            {isDragging && (
              <div className="absolute inset-0 bg-primary/5 border-2 border-dashed border-primary flex items-center justify-center pointer-events-none">
                <p className="text-primary font-medium">Sleep hier om toe te voegen</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <DesignerRightSidebar
          selectedIndices={selectedIndices}
          placeholders={placeholders}
          onUpdate={handlePlaceholderUpdate}
          onDelete={handlePlaceholderDelete}
          onNudge={handleNudge}
          onCenter={handleCenter}
          nudgeStepSize={nudgeStepSize}
          setNudgeStepSize={setNudgeStepSize}
          availablePlaceholders={availablePlaceholders}
          currentPage={currentPage}
          snapPosition={snapPosition}
          setPlaceholders={setPlaceholders}
          setSelectedIndices={setSelectedIndices}
          setLastSelectedIndex={setLastSelectedIndex}
        />
      </div>

      {/* Bottom Toolbar */}
      <DesignerToolbar
        currentPage={currentPage}
        totalPages={effectivePageCount}
        onPageChange={setCurrentPage}
        scale={scale}
        onScaleChange={setScale}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undoPlaceholders}
        onRedo={redoPlaceholders}
        snapToGrid={snapToGrid}
        onToggleGrid={() => setSnapToGrid(!snapToGrid)}
        gridSize={gridSize}
        onGridSizeChange={setGridSize}
        selectedCount={selectedIndices.size}
        clipboardCount={clipboard.length}
        onCopy={copySelected}
        onPaste={pasteClipboard}
        onDelete={deleteSelected}
        onAlignLeft={alignLeft}
        onAlignRight={alignRight}
        onAlignTop={alignTop}
        onAlignBottom={alignBottom}
        onAlignCenterH={alignCenterH}
        onAlignCenterV={alignCenterV}
        onDistributeH={distributeHorizontally}
        onDistributeV={distributeVertically}
        onToggleGlobal={toggleGlobal}
      />
    </div>
  );
}
